'use client'

import { useState, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import { toast } from '@/components/ui/toast'
import { cn } from '@/lib/utils'

interface CopyButtonProps {
  value: string
  label?: string
  className?: string
  size?: 'sm' | 'default'
}

export function CopyButton({
  value,
  label = 'Copy',
  className,
  size = 'sm',
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      toast.success('Copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }, [value])

  return (
    <Button
      type='button'
      variant='ghost'
      size={size === 'sm' ? 'icon-sm' : 'default'}
      onClick={handleCopy}
      aria-label={copied ? 'Copied' : label}
      className={cn(
        'text-brand-muted hover:text-brand-navy focus-visible:ring-2 focus-visible:ring-brand-cornflower',
        size !== 'sm' && 'gap-2',
        className
      )}
    >
      {copied ? (
        <Icons.check className='h-4 w-4 text-emerald-500' strokeWidth={1.5} />
      ) : (
        <Icons.copy className='h-4 w-4' strokeWidth={1.5} />
      )}
      {size !== 'sm' && <span>{copied ? 'Copied' : label}</span>}
    </Button>
  )
}
